import React from 'react';
import { UserWithRole, Referent } from '../../../../shared/services/userService';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';

interface UserTableProps {
  users: UserWithRole[];
  onRoleChange: (user: UserWithRole) => void;
  onAccessToggle: (user: UserWithRole) => void;
  onViewHistory: (user: UserWithRole) => void;
  onPanelAccess: (user: UserWithRole) => void;
  onReferents: (user: UserWithRole) => void;
  onViewDetails?: (user: UserWithRole) => void;
  onForceLogout?: (userId: number) => void;
  currentUserId?: number;
  currentUserRoleId?: number;
  updating: boolean;
  isTeamMember: (user: UserWithRole) => boolean;
  usersReferents: Map<number, Referent[]>;
  onlineUserIds?: Set<number>;
  readOnly?: boolean;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  onRoleChange,
  onAccessToggle,
  onViewHistory,
  onPanelAccess,
  onReferents,
  onViewDetails,
  onForceLogout,
  currentUserId,
  currentUserRoleId,
  updating,
  isTeamMember,
  usersReferents,
  onlineUserIds,
  readOnly = false
}) => {
  const translationPage = readOnly ? 'UsersManagement' : 'UserRolesManagement';
  const { t } = usePageTranslation('panel', translationPage);

  const getUserReferents = (userId: number): Referent[] => usersReferents.get(userId) || [];

  const getActiveReferentFunctions = (user: UserWithRole): Referent[] =>
    getUserReferents(user.id).filter((ref) => ref.is_active);

  const getRoleBadgeColor = (roleId: number) => {
    switch (roleId) {
      case 1: return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 2: return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 3: return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      case 4: return 'bg-cockpit-violet/20 text-cockpit-violet border-cockpit-violet/50';
      case 5: return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 6: return 'bg-indigo-500/20 text-indigo-400 border-indigo-500/50';
      default: return 'bg-slate-500/20 text-cockpit-text-2 border-slate-500/50';
    }
  };

  return (
    <div className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/90 backdrop-blur-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1a2537]">
              <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">{t('table.username')}</th>
              <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">{t('table.email')}</th>
              <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">{t('table.role')}</th>
              <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">{t('table.referent')}</th>
              <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">{t('table.status')}</th>
              <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">{t('table.created')}</th>
              <th className="text-right py-3 px-4 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">{t('table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b border-[#1a2537]/60 hover:bg-[#0b1220]/40 transition-colors">
                <td className="py-3 px-4 text-cockpit-text">
                  <div className="flex items-center gap-2">
                    <span>{user.username}</span>
                    {onViewDetails && (
                      <button
                        onClick={() => onViewDetails(user)}
                        className="p-1 text-cyan-400 hover:bg-cyan-500/20 rounded transition-colors"
                        title={t('actions.viewDetails')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    )}
                    {onForceLogout && onlineUserIds && onlineUserIds.has(user.id) && user.id !== currentUserId && (
                      <button
                        onClick={() => onForceLogout(user.id)}
                        className="p-1 text-orange-400 hover:bg-orange-500/20 rounded transition-colors"
                        title={t('actions.forceLogout') || 'Déconnecter de force'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4 text-cockpit-text-2 text-sm">{user.email}</td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium border ${getRoleBadgeColor(user.roles_id)}`}>
                    {user.role_name || `Rôle ${user.roles_id}`}
                  </span>
                </td>
                <td className="py-3 px-4">
                  {isTeamMember(user) ? (
                    (() => {
                      const activeReferents = getActiveReferentFunctions(user);
                      if (activeReferents.length > 0) {
                        return (
                          <div className="flex flex-wrap gap-1">
                            {activeReferents.map((referent, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/50"
                                title={referent.description || referent.function}
                              >
                                {referent.function}
                              </span>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-slate-500/20 text-cockpit-text-2 border border-slate-500/50">
                          {t('table.referentNo')}
                        </span>
                      );
                    })()
                  ) : (
                    <span className="text-cockpit-muted">-</span>
                  )}
                </td>
                <td className="py-3 px-4">
                  <div className="flex flex-col gap-1">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        user.activated
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                          : 'bg-red-500/20 text-red-400 border border-red-500/50'
                      }`}
                    >
                      {user.activated ? t('status.active') : t('status.inactive')}
                    </span>
                    {onlineUserIds && onlineUserIds.has(user.id) && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/50">
                        {t('status.online') || 'En ligne'}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4 text-cockpit-text-2 text-sm">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center justify-end space-x-1">
                    {onForceLogout && onlineUserIds && onlineUserIds.has(user.id) && user.id !== currentUserId && (
                      <button
                        onClick={() => onForceLogout(user.id)}
                        className="p-1.5 text-orange-400 hover:bg-orange-500/20 rounded transition-colors"
                        title={t('actions.forceLogout') || 'Déconnecter de force'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </button>
                    )}
                    {!readOnly && (
                      <>
                        <button
                          onClick={() => onRoleChange(user)}
                          className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
                          title={t('actions.changeRole')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => onAccessToggle(user)}
                          disabled={updating || (user.id === currentUserId)}
                          className={`p-1.5 rounded transition-colors ${
                            user.activated
                              ? 'text-red-400 hover:bg-red-500/20'
                              : 'text-emerald-400 hover:bg-emerald-500/20'
                          } ${user.id === currentUserId ? 'opacity-40 cursor-not-allowed' : ''}`}
                          title={user.activated ? t('actions.deactivate') : t('actions.activate')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {user.activated ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            )}
                          </svg>
                        </button>
                        <button
                          onClick={() => onViewHistory(user)}
                          className="p-1.5 text-violet-400 hover:bg-violet-500/20 rounded transition-colors"
                          title={t('actions.viewHistory')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => onPanelAccess(user)}
                          className="p-1.5 text-emerald-400 hover:bg-emerald-500/20 rounded transition-colors"
                          title="Gérer les accès au panel"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </button>
                        {isTeamMember(user) && currentUserRoleId === 1 && (
                          <button
                            onClick={() => onReferents(user)}
                            className="p-1.5 text-yellow-400 hover:bg-yellow-500/20 rounded transition-colors"
                            title="Gérer les référents"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserTable;
